import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ClipboardEvent as RClipboardEvent, KeyboardEvent as RKeyboardEvent, ReactElement } from 'react'
import { colWidthOf, getEvaluator, rowHeightOf, useStore, usedRange } from '../core/store'
import { colName, normalizeRange, ref } from '../core/a1'
import { HEADER_HEIGHT, ROW_HEADER_WIDTH } from '../core/types'
import type { Sheet } from '../core/types'
import { expandForMerges, mergeAt, rangesIntersect } from '../core/merges'

const OVERSCAN = 4

function borderCss(border?: { style: string; color: string }): string | undefined {
  if (!border) return undefined
  const width = border.style === 'medium' ? 2 : border.style === 'thick' || border.style === 'double' ? 3 : 1
  const line = ['dashed', 'dotted', 'double'].includes(border.style) ? border.style : 'solid'
  return `${width}px ${line} ${border.color}`
}

/** off[i] = i번 행/열의 시작 좌표 (1-based, off[1] = 0) */
function buildOffsets(count: number, sizeOf: (i: number) => number): number[] {
  const off = new Array<number>(count + 2)
  off[0] = 0
  off[1] = 0
  for (let i = 1; i <= count; i++) off[i + 1] = off[i] + sizeOf(i)
  return off
}

/** 좌표 → 1-based 인덱스 */
function indexAt(off: number[], count: number, pos: number): number {
  let lo = 1
  let hi = count
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (off[mid] <= pos) lo = mid
    else hi = mid - 1
  }
  return Math.min(Math.max(lo, 1), count)
}

export default function Grid() {
  const wb = useStore((s) => s.wb)
  const version = useStore((s) => s.version)
  const sel = useStore((s) => s.sel)
  const editing = useStore((s) => s.editing)
  const aiTouched = useStore((s) => s.aiTouched)

  const select = useStore((s) => s.select)
  const setEditing = useStore((s) => s.setEditing)
  const commitEdit = useStore((s) => s.commitEdit)
  const setCellsRaw = useStore((s) => s.setCellsRaw)
  const clearRange = useStore((s) => s.clearRange)
  const setColWidth = useStore((s) => s.setColWidth)
  const setRowHeight = useStore((s) => s.setRowHeight)
  const formatRange = useStore((s) => s.formatRange)
  const fillRange = useStore((s) => s.fillRange)
  const undo = useStore((s) => s.undo)
  const redo = useStore((s) => s.redo)
  const updateImage = useStore((s) => s.updateImage)
  const removeImage = useStore((s) => s.removeImage)

  const sheet: Sheet = useMemo(
    () => wb.sheets.find((s) => s.id === wb.activeSheetId) ?? wb.sheets[0],
    [wb]
  )

  const viewRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<HTMLInputElement>(null)
  const [scroll, setScroll] = useState({ x: 0, y: 0 })
  const [viewport, setViewport] = useState({ w: 800, h: 600 })
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  const [fillPreview, setFillPreview] = useState<ReturnType<typeof normalizeRange> | null>(null)
  const dragRef = useRef<{ mode: 'cell' | 'row' | 'col' } | null>(null)
  const resizeRef = useRef<{ axis: 'col' | 'row'; index: number; start: number; base: number } | null>(null)
  const imageDragRef = useRef<{
    mode: 'move' | 'resize'; id: string; startX: number; startY: number
    x: number; y: number; width: number; height: number; group: string
  } | null>(null)
  const fillDragRef = useRef<{ source: ReturnType<typeof normalizeRange>; target: ReturnType<typeof normalizeRange> } | null>(null)

  const colOff = useMemo(
    () => buildOffsets(sheet.cols, (c) => colWidthOf(sheet, c)),
    [sheet.colWidths, sheet.cols, sheet.id]
  )
  const rowOff = useMemo(
    () => buildOffsets(sheet.rows, (r) => rowHeightOf(sheet, r)),
    [sheet.rowHeights, sheet.rows, sheet.id]
  )
  const totalW = colOff[sheet.cols + 1]
  const totalH = rowOff[sheet.rows + 1]

  // 뷰포트 크기 추적
  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setViewport({ w: el.clientWidth, h: el.clientHeight }))
    ro.observe(el)
    setViewport({ w: el.clientWidth, h: el.clientHeight })
    return () => ro.disconnect()
  }, [])

  const c0 = Math.max(1, indexAt(colOff, sheet.cols, scroll.x) - OVERSCAN)
  const c1 = Math.min(sheet.cols, indexAt(colOff, sheet.cols, scroll.x + viewport.w) + OVERSCAN)
  const r0 = Math.max(1, indexAt(rowOff, sheet.rows, scroll.y) - OVERSCAN)
  const r1 = Math.min(sheet.rows, indexAt(rowOff, sheet.rows, scroll.y + viewport.h) + OVERSCAN)
  const visibleRange = { r1: r0, c1: c0, r2: r1, c2: c1 }
  let cellR0 = r0
  let cellC0 = c0
  for (const merge of sheet.merges ?? []) {
    if (!rangesIntersect(visibleRange, merge)) continue
    cellR0 = Math.min(cellR0, merge.r1)
    cellC0 = Math.min(cellC0, merge.c1)
  }

  const evaluator = useMemo(() => getEvaluator(), [version, wb.activeSheetId])
  const range = expandForMerges(
    sheet,
    normalizeRange({ r1: sel.anchor.row, c1: sel.anchor.col, r2: sel.focus.row, c2: sel.focus.col })
  )

  // ── 선택 셀을 화면 안으로 ──────────────────────────────────
  useEffect(() => {
    const el = scrollerRef.current
    if (!el || editing) return
    const { row, col } = sel.focus
    if (row > sheet.rows || col > sheet.cols) return
    const x = colOff[col]
    const w = colWidthOf(sheet, col)
    const y = rowOff[row]
    const h = rowHeightOf(sheet, row)
    let nx = el.scrollLeft
    let ny = el.scrollTop
    if (x < nx) nx = x
    else if (x + w > nx + el.clientWidth) nx = x + w - el.clientWidth
    if (y < ny) ny = y
    else if (y + h > ny + el.clientHeight) ny = y + h - el.clientHeight
    if (nx !== el.scrollLeft || ny !== el.scrollTop) el.scrollTo({ left: nx, top: ny })
  }, [sel.focus.row, sel.focus.col, sheet.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editing) editorRef.current?.focus()
    else viewRef.current?.focus()
  }, [editing])

  // ── 마우스 ────────────────────────────────────────────────
  const posFromEvent = useCallback(
    (e: { clientX: number; clientY: number }) => {
      const el = scrollerRef.current!
      const rect = el.getBoundingClientRect()
      const x = e.clientX - rect.left + el.scrollLeft
      const y = e.clientY - rect.top + el.scrollTop
      return { row: indexAt(rowOff, sheet.rows, y), col: indexAt(colOff, sheet.cols, x) }
    },
    [rowOff, colOff, sheet.rows, sheet.cols]
  )

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (fillDragRef.current) {
        const source = fillDragRef.current.source
        const p = posFromEvent(e)
        const rowDistance = p.row < source.r1 ? source.r1 - p.row : p.row > source.r2 ? p.row - source.r2 : 0
        const colDistance = p.col < source.c1 ? source.c1 - p.col : p.col > source.c2 ? p.col - source.c2 : 0
        const target = rowDistance >= colDistance
          ? normalizeRange({ r1: source.r1, c1: source.c1, r2: p.row, c2: source.c2 })
          : normalizeRange({ r1: source.r1, c1: source.c1, r2: source.r2, c2: p.col })
        fillDragRef.current.target = target
        setFillPreview(target)
        return
      }
      if (imageDragRef.current) {
        const r = imageDragRef.current
        const dx = e.clientX - r.startX
        const dy = e.clientY - r.startY
        if (r.mode === 'move')
          updateImage(sheet.id, r.id, { x: Math.max(0, r.x + dx), y: Math.max(0, r.y + dy) }, r.group)
        else
          updateImage(sheet.id, r.id, { width: Math.max(40, r.width + dx), height: Math.max(40, r.height + dy) }, r.group)
        return
      }
      if (resizeRef.current) {
        const r = resizeRef.current
        if (r.axis === 'col') setColWidth(sheet.id, r.index, r.base + (e.clientX - r.start))
        else setRowHeight(sheet.id, r.index, r.base + (e.clientY - r.start))
        return
      }
      if (!dragRef.current) return
      const p = posFromEvent(e)
      const mode = dragRef.current.mode
      if (mode === 'row') select(useStore.getState().sel.anchor, { row: p.row, col: sheet.cols })
      else if (mode === 'col') select(useStore.getState().sel.anchor, { row: sheet.rows, col: p.col })
      else select(useStore.getState().sel.anchor, p)
    }
    const onUp = () => {
      if (fillDragRef.current) {
        const { source, target } = fillDragRef.current
        fillRange(sheet.id, source, target)
        fillDragRef.current = null
        setFillPreview(null)
      }
      dragRef.current = null
      resizeRef.current = null
      imageDragRef.current = null
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [fillRange, posFromEvent, select, setColWidth, setRowHeight, sheet.id, sheet.cols, sheet.rows, updateImage])

  useEffect(() => setSelectedImageId(null), [sheet.id])

  const startEdit = (row: number, col: number, initial?: string) => {
    const merge = mergeAt(sheet, row, col)
    if (merge) { row = merge.r1; col = merge.c1 }
    const cell = sheet.cells[ref(row, col)]
    const current = cell?.f ?? (cell?.v === null || cell?.v === undefined ? '' : String(cell.v))
    setEditing({ row, col, value: initial !== undefined ? initial : current, replace: initial !== undefined })
  }

  // ── 키보드 ────────────────────────────────────────────────
  const onKeyDown = (e: RKeyboardEvent) => {
    if (editing) return
    const { row, col } = sel.focus
    const ext = e.shiftKey
    const move = (dr: number, dc: number) => {
      const nr = Math.min(Math.max(1, row + dr), sheet.rows)
      const nc = Math.min(Math.max(1, col + dc), sheet.cols)
      if (ext) select(sel.anchor, { row: nr, col: nc })
      else select({ row: nr, col: nc })
      e.preventDefault()
    }

    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase()
      if (k === 'z') return (e.preventDefault(), e.shiftKey ? redo() : undo())
      if (k === 'y') return (e.preventDefault(), redo())
      if (k === 'b') return (e.preventDefault(), toggleStyle('bold'))
      if (k === 'i') return (e.preventDefault(), toggleStyle('italic'))
      if (k === 'u') return (e.preventDefault(), toggleStyle('underline'))
      if (k === 'd' && range.r2 > range.r1)
        return (e.preventDefault(), fillRange(sheet.id, { ...range, r2: range.r1 }, range))
      if (k === 'r' && range.c2 > range.c1)
        return (e.preventDefault(), fillRange(sheet.id, { ...range, c2: range.c1 }, range))
      if (k === 'a') {
        const u = usedRange(sheet)
        e.preventDefault()
        return select({ row: 1, col: 1 }, { row: u?.r2 ?? 1, col: u?.c2 ?? 1 })
      }
      if (k === 'arrowdown') return move(sheet.rows - row, 0)
      if (k === 'arrowup') return move(1 - row, 0)
      if (k === 'arrowright') return move(0, sheet.cols - col)
      if (k === 'arrowleft') return move(0, 1 - col)
      if (k === 'home') return move(1 - row, 1 - col)
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        return move(1, 0)
      case 'ArrowUp':
        return move(-1, 0)
      case 'ArrowRight':
        return move(0, 1)
      case 'ArrowLeft':
        return move(0, -1)
      case 'PageDown':
        return move(20, 0)
      case 'PageUp':
        return move(-20, 0)
      case 'Home':
        return move(0, 1 - col)
      case 'Tab':
        e.preventDefault()
        return select({ row, col: Math.min(sheet.cols, col + (e.shiftKey ? -1 : 1)) })
      case 'Enter':
      case 'F2':
        e.preventDefault()
        return startEdit(row, col)
      case 'Delete':
      case 'Backspace':
        e.preventDefault()
        if (selectedImageId) {
          removeImage(sheet.id, selectedImageId)
          setSelectedImageId(null)
          return
        }
        return clearRange(sheet.id, range, 'contents')
      case 'Escape':
        if (selectedImageId) {
          setSelectedImageId(null)
          return
        }
        return select({ row, col })
      default:
        break
    }

    if (e.key.length === 1 && !e.altKey) {
      e.preventDefault()
      startEdit(row, col, e.key)
    }
  }

  const toggleStyle = (key: 'bold' | 'italic' | 'underline') => {
    const cur = sheet.cells[ref(sel.focus.row, sel.focus.col)]?.s?.[key]
    formatRange(sheet.id, range, { [key]: !cur } as any)
  }

  // ── 복사 / 붙여넣기 ───────────────────────────────────────
  const onCopy = (e: RClipboardEvent) => {
    if (editing) return
    const lines: string[] = []
    for (let r = range.r1; r <= range.r2; r++) {
      const cells: string[] = []
      for (let c = range.c1; c <= range.c2; c++) cells.push(evaluator.display(sheet.name, r, c).text)
      lines.push(cells.join('\t'))
    }
    e.clipboardData.setData('text/plain', lines.join('\n'))
    e.preventDefault()
  }

  const onCut = (e: RClipboardEvent) => {
    onCopy(e)
    clearRange(sheet.id, range, 'contents')
  }

  const onPaste = (e: RClipboardEvent) => {
    if (editing) return
    const text = e.clipboardData.getData('text/plain')
    if (!text) return
    e.preventDefault()
    const rows = text.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n')
    const entries: Array<{ row: number; col: number; input: string }> = []
    rows.forEach((line, i) => {
      line.split('\t').forEach((v, j) => {
        entries.push({ row: range.r1 + i, col: range.c1 + j, input: v })
      })
    })
    setCellsRaw(sheet.id, entries, '붙여넣기')
    select(
      { row: range.r1, col: range.c1 },
      { row: range.r1 + rows.length - 1, col: range.c1 + Math.max(...rows.map((l) => l.split('\t').length)) - 1 }
    )
  }

  // ── 렌더 ──────────────────────────────────────────────────
  const cells: ReactElement[] = []
  const now = Date.now()
  for (let row = cellR0; row <= r1; row++) {
    for (let col = cellC0; col <= c1; col++) {
      const key = ref(row, col)
      const merge = mergeAt(sheet, row, col)
      if (merge && (merge.r1 !== row || merge.c1 !== col)) continue
      const cell = sheet.cells[key]
      if (!cell && !merge) continue
      const out = evaluator.display(sheet.name, row, col)
      if (out.text === '' && !cell?.s && !merge) continue
      const s = cell?.s
      const isNum = typeof out.value === 'number' && !s?.align
      const touched = (aiTouched[`${sheet.id}!${key}`] ?? 0) > now
      cells.push(
        <div
          key={key}
          className={`cell${merge ? ' merged' : ''}${isNum ? ' num' : ''}${out.error ? ' err' : ''}${touched ? ' ai-touched' : ''}`}
          style={{
            left: colOff[col],
            top: rowOff[row],
            width: merge ? colOff[merge.c2 + 1] - colOff[merge.c1] : colWidthOf(sheet, col),
            height: merge ? rowOff[merge.r2 + 1] - rowOff[merge.r1] : rowHeightOf(sheet, row),
            fontWeight: s?.bold ? 700 : undefined,
            fontStyle: s?.italic ? 'italic' : undefined,
            textDecoration: s?.underline ? 'underline' : undefined,
            color: s?.color,
            // 병합 셀은 빈 셀용 배경 격자선보다 위에서 불투명하게 덮어야
            // 내부 열/행 경계가 비치지 않고 실제 한 셀처럼 보인다.
            background: merge ? (s?.bg ?? '#ffffff') : s?.bg,
            fontSize: s?.fontSize,
            fontFamily: s?.fontFamily,
            alignItems: s?.verticalAlign === 'top' ? 'flex-start'
              : s?.verticalAlign === 'bottom' ? 'flex-end' : undefined,
            whiteSpace: s?.wrap ? 'pre-wrap' : undefined,
            overflowWrap: s?.wrap ? 'anywhere' : undefined,
            paddingLeft: s?.indent ? 5 + s.indent * 12 : undefined,
            zIndex: merge ? 1 : undefined,
            borderTop: borderCss(s?.borderTop),
            borderRight: borderCss(s?.borderRight),
            borderBottom: borderCss(s?.borderBottom),
            borderLeft: borderCss(s?.borderLeft),
            justifyContent:
              s?.align === 'center' ? 'center' : s?.align === 'right' ? 'flex-end' : isNum ? 'flex-end' : undefined,
          }}
          title={cell?.f ?? undefined}
        >
          <span className="cell-text" style={{ transform: s?.textRotation ? `rotate(${-s.textRotation}deg)` : undefined }}>
            {out.text}
          </span>
        </div>
      )
    }
  }

  // 빈 셀에도 격자선이 보이도록 배경 격자를 그린다
  const gridLines: ReactElement[] = []
  for (let col = c0; col <= c1; col++)
    gridLines.push(
      <div
        key={`v${col}`}
        style={{
          position: 'absolute',
          left: colOff[col + 1] - 1,
          top: rowOff[r0],
          width: 1,
          height: rowOff[r1 + 1] - rowOff[r0],
          background: 'var(--line)',
        }}
      />
    )
  for (let row = r0; row <= r1; row++)
    gridLines.push(
      <div
        key={`h${row}`}
        style={{
          position: 'absolute',
          top: rowOff[row + 1] - 1,
          left: colOff[c0],
          height: 1,
          width: colOff[c1 + 1] - colOff[c0],
          background: 'var(--line)',
        }}
      />
    )

  const colHeaders: ReactElement[] = []
  for (let col = c0; col <= c1; col++) {
    const w = colWidthOf(sheet, col)
    const on = col >= range.c1 && col <= range.c2
    colHeaders.push(
      <div
        key={col}
        className={`hdr-cell${on ? ' sel' : ''}`}
        style={{ left: colOff[col], top: 0, width: w, height: HEADER_HEIGHT }}
        onMouseDown={(e) => {
          e.preventDefault()
          viewRef.current?.focus()
          dragRef.current = { mode: 'col' }
          select({ row: 1, col }, { row: sheet.rows, col })
        }}
      >
        {colName(col)}
        <div
          className="hdr-resize col"
          style={{ left: w - 3 }}
          onMouseDown={(e) => {
            e.stopPropagation()
            e.preventDefault()
            resizeRef.current = { axis: 'col', index: col, start: e.clientX, base: w }
          }}
        />
      </div>
    )
  }

  const rowHeaders: ReactElement[] = []
  for (let row = r0; row <= r1; row++) {
    const h = rowHeightOf(sheet, row)
    const on = row >= range.r1 && row <= range.r2
    rowHeaders.push(
      <div
        key={row}
        className={`hdr-cell${on ? ' sel' : ''}`}
        style={{ top: rowOff[row], left: 0, width: ROW_HEADER_WIDTH, height: h }}
        onMouseDown={(e) => {
          e.preventDefault()
          viewRef.current?.focus()
          dragRef.current = { mode: 'row' }
          select({ row, col: 1 }, { row, col: sheet.cols })
        }}
      >
        {row}
        <div
          className="hdr-resize row"
          style={{ top: h - 3 }}
          onMouseDown={(e) => {
            e.stopPropagation()
            e.preventDefault()
            resizeRef.current = { axis: 'row', index: row, start: e.clientY, base: h }
          }}
        />
      </div>
    )
  }

  const selBox = {
    left: colOff[range.c1],
    top: rowOff[range.r1],
    width: colOff[Math.min(range.c2, sheet.cols) + 1] - colOff[range.c1],
    height: rowOff[Math.min(range.r2, sheet.rows) + 1] - rowOff[range.r1],
  }
  const activeMerge = mergeAt(sheet, sel.focus.row, sel.focus.col)
  const activeBox = activeMerge
    ? {
        left: colOff[activeMerge.c1],
        top: rowOff[activeMerge.r1],
        width: colOff[activeMerge.c2 + 1] - colOff[activeMerge.c1],
        height: rowOff[activeMerge.r2 + 1] - rowOff[activeMerge.r1],
      }
    : {
        left: colOff[sel.focus.col],
        top: rowOff[sel.focus.row],
        width: colWidthOf(sheet, sel.focus.col),
        height: rowHeightOf(sheet, sel.focus.row),
      }
  const fillBox = fillPreview ? {
    left: colOff[fillPreview.c1],
    top: rowOff[fillPreview.r1],
    width: colOff[fillPreview.c2 + 1] - colOff[fillPreview.c1],
    height: rowOff[fillPreview.r2 + 1] - rowOff[fillPreview.r1],
  } : null

  return (
    <div
      className="sheet-view"
      ref={viewRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onCopy={onCopy}
      onCut={onCut}
      onPaste={onPaste}
    >
      <div className="corner-box" style={{ width: ROW_HEADER_WIDTH, height: HEADER_HEIGHT }} onMouseDown={(e) => {
        e.preventDefault()
        select({ row: 1, col: 1 }, { row: sheet.rows, col: sheet.cols })
      }} />

      <div
        className="hdr-strip cols"
        style={{ left: ROW_HEADER_WIDTH, top: 0, right: 0, height: HEADER_HEIGHT }}
      >
        <div className="hdr-inner" style={{ transform: `translateX(${-scroll.x}px)`, width: totalW, height: HEADER_HEIGHT }}>
          {colHeaders}
        </div>
      </div>

      <div
        className="hdr-strip rows"
        style={{ left: 0, top: HEADER_HEIGHT, bottom: 0, width: ROW_HEADER_WIDTH }}
      >
        <div className="hdr-inner" style={{ transform: `translateY(${-scroll.y}px)`, width: ROW_HEADER_WIDTH, height: totalH }}>
          {rowHeaders}
        </div>
      </div>

      <div
        className="sheet-scroller"
        ref={scrollerRef}
        style={{ left: ROW_HEADER_WIDTH, top: HEADER_HEIGHT, right: 0, bottom: 0 }}
        onScroll={(e) => {
          const el = e.currentTarget
          setScroll({ x: el.scrollLeft, y: el.scrollTop })
        }}
        onMouseDown={(e) => {
          if (e.button !== 0) return
          const p = posFromEvent(e)
          viewRef.current?.focus()
          if (editing) commitEdit(editing.value, 'none')
          dragRef.current = { mode: 'cell' }
          if (e.shiftKey) select(sel.anchor, p)
          else select(p)
        }}
        onDoubleClick={(e) => {
          const p = posFromEvent(e)
          startEdit(p.row, p.col)
        }}
      >
        <div className="sheet-canvas" style={{ width: totalW, height: totalH }}>
          {gridLines}
          {cells}
          {(sheet.images ?? []).map((image) => {
            const selected = selectedImageId === image.id
            return (
              <div
                key={image.id}
                className={`sheet-image${selected ? ' selected' : ''}`}
                style={{ left: image.x, top: image.y, width: image.width, height: image.height }}
                title={`${image.name} · 드래그해서 이동 · Delete로 삭제`}
                onMouseDown={(e) => {
                  if (e.button !== 0) return
                  e.preventDefault()
                  e.stopPropagation()
                  setSelectedImageId(image.id)
                  imageDragRef.current = {
                    mode: 'move', id: image.id, startX: e.clientX, startY: e.clientY,
                    x: image.x, y: image.y, width: image.width, height: image.height,
                    group: `image-${image.id}-${Date.now()}`,
                  }
                }}
              >
                <img src={image.src} alt={image.name} draggable={false} />
                {selected && (
                  <>
                    <button
                      className="sheet-image-delete" type="button" aria-label="이미지 삭제" title="이미지 삭제"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        removeImage(sheet.id, image.id)
                        setSelectedImageId(null)
                      }}
                    >×</button>
                    <div
                      className="sheet-image-resize" title="드래그해서 크기 조절"
                      onMouseDown={(e) => {
                        if (e.button !== 0) return
                        e.preventDefault()
                        e.stopPropagation()
                        imageDragRef.current = {
                          mode: 'resize', id: image.id, startX: e.clientX, startY: e.clientY,
                          x: image.x, y: image.y, width: image.width, height: image.height,
                          group: `image-${image.id}-${Date.now()}`,
                        }
                      }}
                    />
                  </>
                )}
              </div>
            )
          })}
          <div className="sel-rect" style={selBox} />
          {fillBox && <div className="fill-preview" style={fillBox} />}
          <div className="sel-active" style={activeBox} />
          {!editing && (
            <div
              className="fill-handle"
              title="드래그하여 자동 채우기"
              style={{ left: selBox.left + selBox.width - 4, top: selBox.top + selBox.height - 4 }}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                fillDragRef.current = { source: range, target: range }
                setFillPreview(range)
              }}
            />
          )}
          {editing && (
            <input
              ref={editorRef}
              className="cell-editor"
              style={{
                left: colOff[editing.col],
                top: rowOff[editing.row],
                minWidth: (() => {
                  const m = mergeAt(sheet, editing.row, editing.col)
                  return m ? colOff[m.c2 + 1] - colOff[m.c1] : colWidthOf(sheet, editing.col)
                })(),
                height: (() => {
                  const m = mergeAt(sheet, editing.row, editing.col)
                  return m ? rowOff[m.r2 + 1] - rowOff[m.r1] : rowHeightOf(sheet, editing.row)
                })(),
              }}
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitEdit(editing.value, e.shiftKey ? 'none' : 'down')
                } else if (e.key === 'Tab') {
                  e.preventDefault()
                  commitEdit(editing.value, 'right')
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditing(null)
                  viewRef.current?.focus()
                }
                e.stopPropagation()
              }}
              onBlur={() => editing && commitEdit(editing.value, 'none')}
            />
          )}
        </div>
      </div>
    </div>
  )
}
